import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  List,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import SwapVertOutlined from "@mui/icons-material/SwapVertOutlined";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import { useAccountPositionStore } from "../../Store/accountPositionStore";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import { useAuth } from "../../Context/AuthProvider";
import { tokens } from "../../Common/designTokens"; // already present

const AccountList = ({
  accounts,
  selectedAccount,
  onSelectAccount,
  loading,
}) => {
  const {
    setAddAccountModalOpen,
    setEditAccountModalOpen,
    getAccountForEdit,
    fetchAccountPositions,
    loadMoreAccounts,
    updateAccountsFilter,
    accountsPagination,
    accountsFilter,
  } = useAccountPositionStore();

  const { userId } = useAuth();
  const token = localStorage.getItem("token");

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [pendingEditAccount, setPendingEditAccount] = useState(null);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("default"); // Changed initial state to 'default'
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  // Add ref for scroll container
  const scrollContainerRef = useRef(null);

  // Function to check if user can edit an account
  const canEditAccount = (account) => {
    // if(account.entryBy != null){
    //   return account.entryBy == userId;
    // }
    // // if isEditable is present then use it
    // if(account.isEditable){
    //   return account.isEditable === 1;
    // }
    return true;
  };

  const handleEditClick = async (event, account) => {
    event.stopPropagation();

    if (isEditLoading) return;

    if (selectedAccount === account.id) {
      const accountForEdit = getAccountForEdit(account.id);
      // NEW: Pass flag to indicate header-only edit
      setEditAccountModalOpen(true, accountForEdit);
    } else {
      try {
        setIsEditLoading(true);
        setPendingEditAccount(account);
        onSelectAccount(account.id);

        await new Promise((resolve) => setTimeout(resolve, 300));

        await new Promise((resolve, reject) => {
          const token = localStorage.getItem("token");
          fetchAccountPositions(account.id, token)
            .then(() => resolve())
            .catch((error) => reject(error));
        });

        const accountForEdit = getAccountForEdit(account.id);
        // NEW: Pass flag to indicate header-only edit
        setEditAccountModalOpen(true, accountForEdit);
      } catch (error) {
        console.error("Error loading account data for edit:", error);
      } finally {
        setIsEditLoading(false);
        setPendingEditAccount(null);
      }
    }
  };
  const handleDropdownClick = (e) => {
    e.stopPropagation();
    setIsSelectOpen(true);
  };
  const handleDeleteClick = (event, account) => {
    event.stopPropagation();
    setAccountToDelete(account);
    setDeleteConfirmOpen(true);
  };

  const handleAddAccount = () => {
    setAddAccountModalOpen(true);
  };

  const handleCloseDeleteDialog = (success) => {
    setDeleteConfirmOpen(false);
    setAccountToDelete(null);
    // Success message is now handled by the store
  };

  const resetScrollAndUpdateFilter = (updateParams) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;

      requestAnimationFrame(() => {
        updateAccountsFilter({ ...updateParams, forceReset: true });
      });
    } else {
      updateAccountsFilter({ ...updateParams, forceReset: true });
    }
  };

  const handleSortClick = () => {
    const nextSortOrder = (() => {
      switch (sortOrder) {
        case "default": return "asc";
        case "asc": return "desc";
        case "desc": return "default";
        default: return "default";
      }
    })();

    setSortOrder(nextSortOrder);
    resetScrollAndUpdateFilter({ sortDirection: nextSortOrder });
  };

  // const handlePMChange = (e) => {
  //   const newPmId = e.target.value;
  //   setSelectedPM(newPmId);
  //   resetScrollAndUpdateFilter({ pmId: newPmId || 0 });
  // };

  // const handleClearPM = () => {
  //   setSelectedPM("");
  //   resetScrollAndUpdateFilter({ pmId: 0 });
  // };


  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 50; // Trigger when 50px from bottom

    if (scrollHeight - scrollTop - clientHeight < threshold) {
      if (accountsPagination.hasMore && !accountsPagination.isLoadingMore) {
        loadMoreAccounts();
      }
    }
  }, [
    accountsPagination.hasMore,
    accountsPagination.isLoadingMore,
    loadMoreAccounts,
  ]);

  // Add scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  // Sync local sort state with store
  useEffect(() => {
    setSortOrder(accountsFilter.sortDirection);
  }, [accountsFilter.sortDirection]);

  // Sync local PM state with store
  // useEffect(() => {
  //   setSelectedPM(accountsFilter.pmId === 0 ? "" : accountsFilter.pmId);
  // }, [accountsFilter.pmId]);

  // Filter accounts - remove local sorting since it's handled by server
  const filteredAccounts = useMemo(() => {
    // Since filtering and sorting are now done on server, just return all accounts
    return accounts;
  }, [accounts]);

  // helper to produce white with alpha from tokens.colors.white (which is 'rgba(255,255,255,1)')
  const whiteWithAlpha = (alpha) =>
    tokens.colors.white.replace(/1\)$/, `${alpha})`);

  // new helper to produce font color with alpha from tokens.colors.fontColour (rgba(51,51,51,1))
  const fontWithAlpha = (alpha) =>
    tokens.colors.fontColour.replace(/1\)$/, `${alpha})`);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        px: 2,
        py: 1,
        // ensure container doesn't force dark bg; allow parent to control or use page background
        backgroundColor: "transparent",
      }}
    >
      {/* Header with sort icon on the right */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{ color: tokens.colors.fontColour, fontWeight: "bold",pt:0.5 }}
        >
          List of Accounts
        </Typography>
        <Box
          onClick={handleSortClick}
          sx={{
            width: 40,
            height: 36,
            border:
              sortOrder !== "default"
                ? `1px solid ${tokens.palette.primaryA30}`
                : `1px solid ${tokens.colors.bodyBorderColor50}`,
            borderRadius: "8px", // <-- changed from 4px
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor:
              sortOrder !== "default"
                ? tokens.palette.primaryA04
                : "transparent",
            cursor: "pointer",
            "&:hover": {
              bgcolor:
                sortOrder !== "default"
                  ? tokens.palette.primaryA10
                  : tokens.colors.defaultGridHoverColor, // light hover tone
            },
          }}
        >
          {sortOrder === "default" ? (
            <SwapVertOutlined
              sx={{
                fontSize: 16,
                color: sortOrder !== "default" ? tokens.palette.primary : tokens.colors.darkgray,
              }}
            />
          ) : sortOrder === "asc" ? (
            <ArrowUpwardOutlinedIcon sx={{ fontSize: 16, color: tokens.palette.primary }} />
          ) : (
            <ArrowDownwardOutlinedIcon
              sx={{ fontSize: 16, color: tokens.palette.primary }}
            />
          )}
        </Box>
      </Box>

      {/* Filter Section - remove the sort button from here */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        {/* Removed sort button code */}
        {/* <FormControl size="small" sx={{ flex: 1, position: "relative" }}>
          <Select
            value={selectedPM}
            onChange={handlePMChange}
            displayEmpty
            open={isSelectOpen}
            onClose={() => setIsSelectOpen(false)}
            onOpen={() => setIsSelectOpen(true)}
            IconComponent={() =>
              selectedPM ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearPM();
                  }}
                  sx={{
                    position: "absolute",
                    right: 8,
                    color: fontWithAlpha(0.7),
                    padding: "2px",
                    "&:hover": {
                      color: tokens.colors.fontColour,
                      backgroundColor: whiteWithAlpha(0.05),
                    },
                  }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  onClick={handleDropdownClick}
                  sx={{
                    position: "absolute",
                    right: 8,
                    padding: "2px",
                    "&:hover": {
                      backgroundColor: whiteWithAlpha(0.05),
                    },
                  }}
                >
                  <KeyboardArrowDownOutlinedIcon
                    sx={{
                      color: fontWithAlpha(0.7),
                      fontSize: 20,
                    }}
                  />
                </IconButton>
              )
            }
            sx={{
              width: 194,
              height: 36,
              color: tokens.colors.fontColour,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: whiteWithAlpha(0.23),
                borderWidth: "1px",
                borderRadius: "8px", // <-- changed from 4px
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: tokens.colors.bodyBorderColor,
              },
            }}
          >
            <MenuItem value="">
              <Typography color="textSecondary">Select PM</Typography>
            </MenuItem>
            {pmUsers.map((pm) => (
              <MenuItem key={pm.userId} value={pm.userId}>
                {pm.userName}
              </MenuItem>
            ))}
          </Select>
        </FormControl> */}
      </Box>

      {/* Separator Line */}
      <Box
        sx={{
          width: "100%",
          height: "1px",
          bgcolor: tokens.colors.bodyBorderColor50,
          mb: 1,
        }}
      />

      {/* Scrollable accounts list */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flexGrow: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          mb: 2,
          minHeight: 0, // Important for proper flex behavior
          position: "relative", // For loading overlay
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: tokens.colors.scrollbarTrack,
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: tokens.colors.scrollbarThumb,
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: tokens.colors.scrollbarThumb,
            },
          },
        }}
      >
        {/* Loading overlay for account fetching */}
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: whiteWithAlpha(0.95),
              zIndex: 1,
              gap: 1,
            }}
          >
            <CircularProgress
              size={40}
              sx={{ color: tokens.palette.primary }}
            />
            <Typography
              variant="body2"
              sx={{ color: tokens.colors.fontColour90 }}
            >
              Loading accounts...
            </Typography>
          </Box>
        )}

        {/* Content */}
        {!loading && filteredAccounts.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: tokens.colors.fontColour90 }}
            >
              No accounts available
            </Typography>
          </Box>
        ) : (
          <>
            {/* if account lengthe is 0 then show that no account found for this user */}
            {accounts.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: tokens.colors.fontColour90 }}
                >
                  No accounts found for this User
                </Typography>
              </Box>
            ) : (
              <>
                <List sx={{ width: "100%", padding: 0, flex: 1 }}>
                  {filteredAccounts.map((account) => (
                    <Box
                      key={account.id}
                      onClick={() => onSelectAccount(account.id)}
                      sx={{
                        width: "100%",
                        height: "56px",
                        border:
                          selectedAccount === account.id
                            ? `1px solid ${tokens.palette.primaryA30}`
                            : `1px solid ${tokens.colors.bodyBorderColor50}`,
                        borderRadius: "8px", // <-- changed from 4px
                        mb: "10px",
                        p: "8px 10px",
                        cursor: "pointer",
                        bgcolor:
                          selectedAccount === account.id
                            ? tokens.palette.primaryA04
                            : tokens.colors.defaultGridRowColor,
                        "&:hover": {
                          bgcolor:
                            selectedAccount === account.id
                              ? tokens.palette.primaryA30
                              : tokens.colors.defaultGridHoverColor,
                        },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        // Highlight account being prepared for edit
                        ...(pendingEditAccount?.id === account.id && {
                          border: `1px solid ${tokens.palette.primary}`,
                          bgcolor: tokens.palette.primaryA04,
                        }),
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ color: tokens.colors.fontColour, fontWeight: "bold" }}
                        >
                          {account.accountNumber ||
                            account.accountIdentifier ||
                            "No account number"}
                        </Typography>
                        {canEditAccount(account) ? (
                          <Box>
                            <IconButton
                              size="small"
                              onClick={(e) => handleEditClick(e, account)}
                              sx={{
                                color: pendingEditAccount?.id === account.id
                                  ? tokens.palette.primaryA80
                                  : fontWithAlpha(0.7),
                                padding: "4px",
                              }}
                              disabled={isEditLoading}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => handleDeleteClick(e, account)}
                              sx={{ padding: "4px" }}
                              disabled={isEditLoading}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : null}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ color: fontWithAlpha(0.7) }}
                      >
                        {account.description || "---"}
                      </Typography>
                    </Box>
                  ))}
                </List>

                {/* Loading indicator for infinite scroll */}
                {accountsPagination.isLoadingMore && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 2,
                    }}
                  >
                    <CircularProgress
                      size={24}
                      sx={{ color: tokens.palette.primary }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: tokens.colors.fontColour90, ml: 1 }}
                    >
                      Loading more accounts...
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>

      {/* Add Demo Account button - stays at bottom */}
      <Button
        startIcon={<AddIcon />}
        variant="contained"
        color="primary"
        sx={{
          width: "100%",
          height: 40,
          borderRadius: "8px", // <-- changed from 4px
          padding: "12px 10px",
          flexShrink: 0,
          position: "relative",
          zIndex: 1001, // Higher than pagination's z-index of 1000
          "& .MuiButton-startIcon": {
            marginRight: "10px",
          },
        }}
        onClick={handleAddAccount}
        disabled={isEditLoading}
      >
        Add Demo Account
      </Button>

      {/* Modals and dialogs */}
      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteDialog}
        account={accountToDelete}
      />
    </Box>
  );
};

export default AccountList;
